import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../utilities/axios";

const initialState = {
  allAuthorisations: [],
  authorisation: [],
  myAuthorisation: [],
  monthlyProgress: null,
  status: "idle",
  error: null,
  // Paginated authorisations data
  paginatedAuthorisations: [],
  paginatedAuthorisationsStatus: "idle",
  paginatedAuthorisationsError: null,
  authorisationsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  },
};

export const getAllAuthorisations = createAsyncThunk(
  "authorisations/getAllAuthorisations",
  async () => {
    let data;
    try {
      const response = await axios.get(`/authorisations`);
      data = await response.data;

      if (response.status === 200) {
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const fetchPaginatedAuthorisations = createAsyncThunk(
  "authorisations/fetchPaginatedAuthorisations",
  async (params) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.search) queryParams.append("search", params.search);
      if (params.status) queryParams.append("status", params.status);
      if (params.department) queryParams.append("department", params.department);
      if (params.dateFrom) queryParams.append("dateFrom", params.dateFrom);
      if (params.dateTo) queryParams.append("dateTo", params.dateTo);
      if (params.userId) queryParams.append("userId", params.userId);
      if (params.headquarterId) queryParams.append("headquarterId", params.headquarterId);

      const response = await axios.get(`/authorisations/paginated?${queryParams.toString()}`);
      
      if (response.status === 200) {
        return {
          data: response.data.data || [],
          pagination: response.data.pagination || { page: 1, limit: 10, total: 0, pages: 0 }
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.response?.data?.message || err.message || "Failed to fetch authorisations"
      );
    }
  }
);

export const addAuthorisation = createAsyncThunk(
  "authorisations/addAuthorisation",
  async (body,thunkAPI) => {
    let data;
    try {
      const response = await axios.post(`/authorisations`, body);

      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(getMyAuthorisations());
        return {
          data: data,
          message: "Your authorisation request is successfully submited ! ",
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const approveAuthorisation = createAsyncThunk(
  "authorisations/approveAuthorisation",
  async (id, thunkAPI) => {
    let data;
    try {
      const response = await axios.patch(`/authorisations/approve/${id}`);

      data = await response.data;

      if (response.status === 200) {
        thunkAPI.dispatch(getAllAuthorisations());
        return { message: data.message || "Authorisation approved successfully" };
      }

      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.response?.data?.message || err.message || "Failed to approve authorisation"
      );
    }
  }
);
export const rejectAuthorisationRequest = createAsyncThunk(
  "authorisations/rejectAuthorisationRequest",
  async (data, thunkAPI) => {
    const { id, descriptionReject } = data;
    try {
      const response = await axios.patch(
        `/authorisations/reject/${id}`,
        { descriptionReject: descriptionReject || "No reason provided" }
      );
      const responseData = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(getAllAuthorisations());
        return { message: responseData.message || "Authorisation rejected successfully" };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.response?.data?.message || err.message || "Failed to reject authorisation"
      );
    }
  }
);

export const deleteAuthorisation = createAsyncThunk(
  "authorisations/deleteAuthorisation",
  async (id, thunkAPI) => {
    let data;
    try {
      const response = await axios.patch(`/authorisations/delete/${id}`);
      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(getAllAuthorisations());
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const getMyAuthorisations = createAsyncThunk(
  "authorisations/getMyLeaves",
  async () => {
    let data;
    try {
      const response = await axios.get(`/authorisations/my-authorisations`);
      data = await response.data;

      if (response.status === 200) {
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const editAuthorisationCurrentUser = createAsyncThunk(
  "authorisations/editAuthorisationCurrentUser",
  async (body, thunkAPI) => {
    let data;
    const { description, startTime, endTime, date } = body.Data;
    try {
      const response = await axios.patch(`/authorisations/edit/${body.id}`, {
        description,
        date,
        startTime,
        endTime,
      });

      data = await response.data;

      if (response.status === 200) {
        thunkAPI.dispatch(getMyAuthorisations());
        return {
          data: data,
          message: "Your authorisation request has been updated successfully.",
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const getMonthlyAuthorizationProgress = createAsyncThunk(
  "authorisations/getMonthlyAuthorizationProgress",
  async (params) => {
    let data;
    try {
      const { month, year } = params;
      
      const response = await axios.get(`/authorisations/monthly-progress?month=${month}&year=${year}`);
      data = await response.data;
      

      if (response.status === 200) {
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      console.error('getMonthlyAuthorizationProgress error:', err);
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const getMyMonthlyAuthorizationProgress = createAsyncThunk(
  "authorisations/getMyMonthlyAuthorizationProgress",
  async (params) => {
    let data;
    try {
      const { month, year } = params;
      
      const response = await axios.get(`/authorisations/monthly-progress/my?month=${month}&year=${year}`);
      data = await response.data;
      

      if (response.status === 200) {
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      console.error('getMyMonthlyAuthorizationProgress error:', err);
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

export const getUserMonthlyAuthorizationProgress = createAsyncThunk(
  "authorisations/getUserMonthlyAuthorizationProgress",
  async (params) => {
    let data;
    try {
      const { month, year, userId, authorizationId } = params;
      
      let url = `/authorisations/monthly-progress/user?month=${month}&year=${year}`;
      if (userId) url += `&userId=${userId}`;
      if (authorizationId) url += `&authorizationId=${authorizationId}`;
      
      const response = await axios.get(url);
      data = await response.data;
      

      if (response.status === 200) {
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      console.error('getUserMonthlyAuthorizationProgress error:', err);
      return Promise.reject(
        err.message
          ? err.message
          : err
          ? err
          : "Oups !Something went wrong! please try again"
      );
    }
  }
);

const slice = createSlice({
  name: "authorisation",
  initialState,
  reducers: {
    handleNewAuthorisation: (state, action) => {
      state.allAuthorisations.push(action.payload.newAuthorisation);
    },
    clearMonthlyProgress: (state) => {
      state.monthlyProgress = null;
    },
  },
  extraReducers: {
    [getAllAuthorisations.pending]: (state) => {
      state.status = "loading";
    },
    [getAllAuthorisations.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.allAuthorisations = action.payload;
    },
    [getAllAuthorisations.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },

    [addAuthorisation.pending]: (state) => {
      state.status = "loading";
    },
    [addAuthorisation.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.authorisation = action.payload;
    },
    [addAuthorisation.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },
    [getMyAuthorisations.pending]: (state) => {
      state.status = "loading";
    },
    [getMyAuthorisations.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.myAuthorisation = action.payload?.authorisations ? { authorisations: action.payload.authorisations } : action.payload;
    },
    [getMyAuthorisations.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },

    [getMonthlyAuthorizationProgress.pending]: (state) => {
      state.status = "loading";
    },
    [getMonthlyAuthorizationProgress.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.monthlyProgress = action.payload;
    },
    [getMonthlyAuthorizationProgress.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },

    [getMyMonthlyAuthorizationProgress.pending]: (state) => {
      state.status = "loading";
    },
    [getMyMonthlyAuthorizationProgress.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.monthlyProgress = action.payload;
    },
    [getMyMonthlyAuthorizationProgress.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },

    [getUserMonthlyAuthorizationProgress.pending]: (state) => {
      state.status = "loading";
    },
    [getUserMonthlyAuthorizationProgress.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.monthlyProgress = action.payload;
    },
    [getUserMonthlyAuthorizationProgress.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },

    [fetchPaginatedAuthorisations.pending]: (state) => {
      state.paginatedAuthorisationsStatus = "loading";
      state.paginatedAuthorisationsError = null;
    },
    [fetchPaginatedAuthorisations.fulfilled]: (state, action) => {
      state.paginatedAuthorisationsStatus = "succeeded";
      state.paginatedAuthorisations = action.payload.data;
      state.authorisationsPagination = action.payload.pagination;
      state.paginatedAuthorisationsError = null;
    },
    [fetchPaginatedAuthorisations.rejected]: (state, action) => {
      state.paginatedAuthorisationsStatus = "failed";
      state.paginatedAuthorisationsError = action.payload;
    },
  },
});
export const { handleNewAuthorisation } = slice.actions;

export const reducer = slice.reducer;

export default slice;

